import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CurrencyInput } from "@/components/CurrencyInput";
import { Plus, Trash2, Star } from "lucide-react";

const newRate = () => ({ id: Math.random().toString(36).slice(2, 10), name: "", rate: 0, role: "", default: false });

export function PayRatesEditor({ value, onChange, params }) {
  const [roles, setRoles] = useState([]);
  useEffect(() => { api.get("/tips/pay-rates/roles", { params: params() }).then((r) => setRoles(r.data)).catch(() => {}); }, []); // eslint-disable-line
  const rates = value?.length ? value : [];
  const set = (i, patch) => onChange(rates.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const setDefault = (i) => onChange(rates.map((r, j) => ({ ...r, default: j === i })));
  const remove = (i) => onChange(rates.filter((_, j) => j !== i));
  const add = () => onChange([...rates, { ...newRate(), default: rates.length === 0 }]);
  return (
    <div className="col-span-2 space-y-2 rounded-lg border border-border p-3" data-testid="pay-rates-editor">
      <div className="flex items-center justify-between"><Label>Pay rates</Label><Button type="button" size="sm" variant="outline" className="h-7" data-testid="add-pay-rate" onClick={add}><Plus className="w-3.5 h-3.5 mr-1" /> Add rate</Button></div>
      <p className="text-xs text-muted-foreground">Add a rate for each job this employee works. Link a rate to a scheduling role so shifts and timesheets pick it automatically; the starred rate is used when no role matches.</p>
      {rates.length === 0 && <p className="text-xs text-muted-foreground" data-testid="pay-rates-empty">Single rate — the hourly rate above applies to all hours.</p>}
      {rates.map((r, i) => (
        <div key={r.id} className="grid grid-cols-[auto_1fr_110px_1fr_auto] gap-2 items-center" data-testid={`pay-rate-row-${i}`}>
          <button type="button" title="Default rate" data-testid={`pay-rate-default-${i}`} onClick={() => setDefault(i)} className={r.default ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}><Star className={`w-4 h-4 ${r.default ? "fill-current" : ""}`} /></button>
          <Input data-testid={`pay-rate-name-${i}`} placeholder="e.g. Server" value={r.name} onChange={(e) => set(i, { name: e.target.value })} />
          <CurrencyInput data-testid={`pay-rate-amount-${i}`} value={r.rate} onChange={(v) => set(i, { rate: v })} />
          <Input data-testid={`pay-rate-role-${i}`} list={`shift-roles-${i}`} placeholder="Shift role (optional)" value={r.role} onChange={(e) => set(i, { role: e.target.value })} />
          <datalist id={`shift-roles-${i}`}>{roles.map((x) => <option key={x} value={x} />)}</datalist>
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-destructive" data-testid={`pay-rate-remove-${i}`} onClick={() => remove(i)}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
    </div>
  );
}

export function TippedToggle({ checked, onChange }) {
  return (
    <label className="col-span-2 flex items-start gap-2 text-sm cursor-pointer rounded-lg border border-border p-3">
      <Checkbox checked={!!checked} onCheckedChange={(v) => onChange(!!v)} data-testid="emp-tipped" className="mt-0.5" />
      <span><span className="font-medium">Tipped employee</span><span className="block text-xs text-muted-foreground">Enables cash-tip reporting at clock-out, tip fields on payroll, and the tip-credit note when the hourly rate is below minimum wage.</span></span>
    </label>
  );
}
